import React from 'react';
import { useSelector } from 'react-redux';

import { Filter } from '@travel/icons/ui';
import { FlatButton, IconText } from '@travel/ui';
import LoadingCircle from '@travel/ui/components/FlatButton/loadingCircle';
import { cx, isEmptyArray } from '@travel/utils';

import WithSkeletonLoading from 'components/WithSkeletonLoading';

import {
  getFilters,
  getFiltersTotal,
  getIsFetchingFilters,
  getIsFilterSelected,
} from 'store/providerList/selectors';

import { Translate } from 'core/translate';

import { PriceRangeType } from '../FilterContainer';
import { FilterFormProps } from '../FilterForm';
import FilterItem from '../FilterItem';

import styles from './filtersList.module.scss';

export type FiltersListProps = {
  setMaxPrice: React.Dispatch<React.SetStateAction<PriceRangeType>>;
  setMinPrice: React.Dispatch<React.SetStateAction<PriceRangeType>>;
  setStarRating: React.Dispatch<React.SetStateAction<Array<string>>>;
  setReviewRating: React.Dispatch<React.SetStateAction<Array<string>>>;
  /** Callback to be called when checkbox value changed */
  onCheckboxChanged?: (featureId: string, groupId: string) => void;
  /** Callback to be called when selected price changed */
  onPriceRangeChanged?: (min?: number, max?: number) => void;
  onClearClick: () => void;
  onSearch: () => void;
};

type Props = {
  className?: string;
  /** Filter search form values for PC/TL */
  filterFormProps: FilterFormProps;
  /** Should show skeleton */
  isLoading: boolean;
} & FiltersListProps;

function FiltersList(props: Props) {
  const filters = useSelector(getFilters);
  const filtersTotal = useSelector(getFiltersTotal);
  const isFetching = useSelector(getIsFetchingFilters);
  const isFilterSelected = useSelector(getIsFilterSelected);

  const { onPriceRangeChanged, onCheckboxChanged, onClearClick, onSearch, isLoading } = props;
  const {
    minPrice,
    maxPrice,
    starRating,
    reviewRating,
    features,
    freeCancellation,
  } = props.filterFormProps;

  // don't show empty filter list
  if (isEmptyArray(filters)) {
    return null;
  }

  return (
    <>
      <div
        className={cx(styles.filterInListWrapper, props.className, 'thin-scrollbar')}
        data-testid="filtersList-wrapper"
      >
        <div className={cx(styles.filterTitle, styles.filterItem)}>
          <IconText
            className={styles.filterTitleIconText}
            spaceBetween={8}
            icon={<Filter size={24} />}
            text={<Translate id="Provider_List.Search.Sort_Filter.Filter_PC_TL" />}
          />
        </div>
        <WithSkeletonLoading
          isLoading={isLoading}
          count={3}
          row={3}
          length="short"
          customStyle="text"
          className={styles.loaderWrapper}
        >
          <FilterItem
            className={styles.filterItem}
            filter={filters[0]}
            minPrice={minPrice}
            maxPrice={maxPrice}
            onPriceRangeChanged={onPriceRangeChanged}
            isInFilterList={true}
          />

          {filters[1] && (
            <FilterItem
              className={styles.filterItem}
              filter={filters[1]}
              starRating={starRating}
              reviewRating={reviewRating}
              features={features}
              onCheckboxChanged={onCheckboxChanged}
              isInFilterList={true}
              freeCancellation={freeCancellation}
            />
          )}

          {filters.slice(2, filters.length).map(filter => (
            <FilterItem
              key={filter.groupId}
              filter={filter}
              className={styles.filterItem}
              starRating={starRating}
              reviewRating={reviewRating}
              features={features}
              onCheckboxChanged={onCheckboxChanged}
              isInFilterList={true}
              freeCancellation={freeCancellation}
            />
          ))}
        </WithSkeletonLoading>
      </div>
      <div className={styles.filterActions}>
        <FlatButton
          className={styles.actionButton}
          classType="secondary"
          onClick={onClearClick}
          type="button"
          data-testid="filterList-clear-flatButton"
        >
          <Translate id="Provider_List.Search.Sort_Filter.Navigation.Clear" />
        </FlatButton>
        <FlatButton
          className={cx(styles.actionButton, isFetching && styles.customLoading)}
          classType="primary"
          onClick={onSearch}
          type="button"
          data-testid="filterList-search-flatButton"
          isDisabled={isFetching}
        >
          <div className={styles.flatButtonContainer}>
            <Translate
              id="Provider_List.Search.Sort_Filter.Filter_PC_TL"
              className={styles.filterTotal}
            />
            {isFetching ? (
              <LoadingCircle classType="primary" />
            ) : isFilterSelected ? (
              `(${filtersTotal})`
            ) : null}
          </div>
        </FlatButton>
      </div>
    </>
  );
}

export default FiltersList;
